import { gql } from '@apollo/client';
import { Badge, Box, SxProps, Typography } from '@mui/material';
import { ImageWithVideoFallback } from 'components/common/Image';
import { useUserContext } from 'contexts/users/User.context/User.context';
import {
  RadarEventReadStatus,
  RadarEventViewType,
  SocialPostFragmentSocialPostCardViewFragmentDoc,
  useGetPaginatedRadarEventsForSlCommunityButtonViewQuery,
} from 'graphql/generated';
import { EventName, useAnalytics } from 'hooks/useAnalytics';
import { Link } from 'react-router-dom';
import { PlotRoutes } from 'Routes';
import { theme } from 'styles/theme';

const PLACEHOLDER_BACKGROUND_COLORS = [
  theme.colors?.utility['purple-1'],
  theme.colors?.utility['orange-1'],
  theme.colors?.utility['teal-1'],
];

// eslint-disable-next-line
gql`
  query GetPaginatedRadarEventsForSLCommunityButtonView(
    $input: PaginatedRadarEventInput!
  ) {
    getPaginatedRadarEvents(input: $input) {
      data {
        id
        socialPost {
          id
          thumbnailUrl
          videoUrl
        }
      }
      meta {
        totalCount
      }
    }
  }
  ${SocialPostFragmentSocialPostCardViewFragmentDoc}
`;

export type SocialListeningCommunityButtonViewProps = {
  brandId: string;
};

export const SocialListeningCommunityButtonView = ({
  brandId,
}: SocialListeningCommunityButtonViewProps) => {
  const { user } = useUserContext();
  const analytics = useAnalytics();

  const { data } = useGetPaginatedRadarEventsForSlCommunityButtonViewQuery({
    variables: {
      input: {
        brandId,
        readStatuses: [RadarEventReadStatus.Unread],
        viewType: RadarEventViewType.KeyEvents,
        take: 3,
      },
    },
  });
  const radarEvents = data?.getPaginatedRadarEvents.data || [];
  const totalCount = data?.getPaginatedRadarEvents.meta.totalCount || 0;

  const renderPost = (index: number) => {
    const post =
      radarEvents?.[index]?.socialPost?.thumbnailUrl ||
      radarEvents?.[index]?.socialPost?.videoUrl;

    if (!post) {
      return (
        <Box
          sx={{
            height: '100%',
            width: '100%',
            bgcolor: PLACEHOLDER_BACKGROUND_COLORS[index],
          }}
        />
      );
    }

    return (
      <ImageWithVideoFallback
        imageUrl={post}
        videoUrl={post}
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: 2,
          objectFit: 'cover',
        }}
      />
    );
  };

  const defaultCardStyles: SxProps = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: 22,
    aspectRatio: '7 / 10',
    borderRadius: 2,
    border: `1px solid ${theme.colors?.primary.white}`,
    overflow: 'hidden',
    boxShadow:
      '0px 8px 16px -6px rgba(24, 39, 75, 0.08), 0px 6px 8px -6px rgba(24, 39, 75, 0.12)',
  };

  return (
    <Badge
      badgeContent={totalCount}
      componentsProps={{
        badge: {
          style: {
            background: theme.colors?.utility['pink-3'],
            color: theme.colors?.primary.parchment,
            ...theme.typography['headline-xxs'],
          },
        },
      }}
    >
      <Box
        component={Link}
        to={PlotRoutes.socialListeningRadar(brandId)}
        onClick={() => {
          analytics.track(EventName.CrmDashboardCommunityUpdatesClicked, {
            brandId,
            userId: user?.id,
          });
        }}
        sx={{
          display: 'flex',
          gap: 3,
          borderRadius: 3,
          background: theme.colors?.utility[275],
          p: theme.spacing(2, 4),
          position: 'relative',
        }}
      >
        <Box
          sx={{
            zIndex: 0,
            position: 'relative',
            aspectRatio: '4 / 3',
            display: 'flex',
            m: theme.spacing(3.5),
          }}
        >
          <Box
            sx={{
              ...defaultCardStyles,
              transform: 'translate(-80%, -50%) rotate(-18.75deg)',
              zIndex: 2,
            }}
          >
            {renderPost(0)}
          </Box>
          <Box
            sx={{
              ...defaultCardStyles,
              transform: 'translate(-50%, -52.5%) rotate(-6.75deg)',
              zIndex: 1,
            }}
          >
            {renderPost(1)}
          </Box>
          <Box
            sx={{
              ...defaultCardStyles,
              transform: 'translate(-20%, -50%) rotate(6.25deg)',
              zIndex: 0,
            }}
          >
            {renderPost(2)}
          </Box>
        </Box>
        <Typography
          variant="headline-sm"
          color={theme.colors?.utility[800]}
          sx={{ alignSelf: 'center' }}
        >
          Community updates
        </Typography>
      </Box>
    </Badge>
  );
};
